import { memo } from 'react';
import { ReactComponent as MapIcon } from '@assets/icons/map-marker-alt.svg';
import { ReactComponent as Star } from '@assets/icons/star.svg';
import { googleMapURL } from 'constant';

import {
  Box,
  Flex,
  Grid,
  GridItem,
  HStack,
  Stack,
  useTheme,
  VStack
} from '@chakra-ui/react';

import {
  formatDistance,
  formatPhoneNumbers,
  formatPhoneToFormat,
  getDistanceInMiles,
  useGeolocation
} from 'utils';

import { LocationInterface } from 'types';

import { Button, FavoriteIconButton, Link, Text } from 'ui';

interface LocationItemProps {
  item: LocationInterface;
  onSelectCard: () => void;
  onToggle: (isSelected: boolean, locationId: number) => void;
  isActive?: boolean;
  isMain?: boolean;
  isLast?: boolean;
  isFavorite?: boolean;
  isLogged: boolean;
  isDistanceHide: boolean;
  isToggling?: boolean;
}

const LocationItem = ({
  item,
  onSelectCard,
  onToggle,
  isActive = false,
  isMain = false,
  isLast = false,
  isFavorite = false,
  isLogged,
  isDistanceHide,
  isToggling = false
}: LocationItemProps) => {
  const { geoLocation } = useGeolocation();
  const {
    colors: {
      palette: {
        components: { locations: styles }
      }
    }
  } = useTheme();

  const itemLongitude = item?.longitude;
  const itemLatitude = item?.latitude;
  const userLatitude = geoLocation?.latitude;
  const userLongitude = geoLocation?.longitude;
  const shouldDisplayDistance =
    userLatitude && userLongitude && itemLongitude && itemLatitude;
  const isValidLocation = item.name && item.city;

  const getUrl = () => {
    const baseUrl = `${googleMapURL}/dir/?api=1`;
    if (itemLatitude && itemLongitude) {
      return `${baseUrl}&destination=${itemLatitude},${itemLongitude}`;
    }
    const address = `${item.addr1 ?? ''}, ${item.city ?? ''}, ${
      item.province ?? ''
    }, ${item.postal ?? ''}, ${item.country ?? ''}`;
    const encodedAddress = encodeURIComponent(address);
    return `${baseUrl}&origin=My+Location&destination=${encodedAddress}`;
  };

  const onFavClick = () => onToggle(isFavorite, item.id);

  const distance =
    shouldDisplayDistance && !isDistanceHide
      ? formatDistance(
          getDistanceInMiles(geoLocation, {
            longitude: itemLongitude,
            latitude: itemLatitude
          })
        )
      : '';

  return isValidLocation ? (
    <Grid
      pl={{ base: 0, sm: '16px', md: '30px' }}
      pr={{ base: '8px', sm: '16px', md: '15px' }}
      py={{ base: '12px', md: '0px' }}
      rowGap={{ base: '5px', md: 0 }}
      templateColumns={{ base: 'repeat(3, 1fr)', md: 'repeat(12, 1fr)' }}
      columnGap={{ base: 0, md: '10px' }}
      backgroundColor={isActive ? styles.activeBg : styles.bg}
      alignItems='center'
      minH={{ base: 'auto', md: '80px' }}
      w='full'
      borderBottom='1px solid'
      borderBottomColor={isLast && isMain ? 'transparent' : styles.itemBorder}
    >
      <GridItem
        colSpan={1}
        rowSpan={{ base: 3, md: 1 }}
        alignContent='center'
        m={{ base: '0 auto', md: 'auto' }}
      >
        <Button
          variant='outline'
          colorScheme='primary'
          onClick={onSelectCard}
          lineHeight='18px'
          fontSize='13px'
          h={{ base: '28px', md: '35px' }}
          w={{ base: '85px', md: '106px' }}
          py={{ base: '6px', md: '9px' }}
          px={{ base: '23px', md: '31px' }}
          isActive={isActive}
        >
          Select
        </Button>
      </GridItem>

      <GridItem
        colSpan={{ base: 2, md: 5 }}
        pl={{ base: 0, md: '41px' }}
        bg='azule'
      >
        <HStack gap='15px'>
          <Text
            fontSize={{ base: '18px', md: '20px', lg: '22px', xl: '25px' }}
            color={styles.locationName}
          >
            {item.name}
          </Text>
          {isLogged && (isMain || Boolean(item.is_primary)) && (
            <Box width={26}>
              <Star fill={styles.favoriteIconColor} width={26} height={25} />
            </Box>
          )}
          {isLogged && !(isMain || Boolean(item.is_primary)) && (
            <Box width={26}>
              <FavoriteIconButton
                onClick={onFavClick}
                fill={
                  isFavorite
                    ? styles.activefavoriteIconColor
                    : styles.favoriteIconColor
                }
                size={26}
                isFavorite={isFavorite}
                isDisable={isToggling}
              />
            </Box>
          )}
        </HStack>
      </GridItem>
      <GridItem colSpan={{ base: 2, md: 5 }} pl={{ base: 0, md: '41px' }}>
        <VStack gap='5px' alignItems='flex-start'>
          <Link
            to={`tel:${formatPhoneNumbers(item.phone)}`}
            sx={{
              fontSize: { base: '15px', md: '17px' },
              fontFamily: 'main',
              _hover: { textDecoration: 'underline' }
            }}
            color={styles.phone}
          >
            {formatPhoneToFormat(formatPhoneNumbers(item.phone))}
          </Link>
          <Link
            sx={{
              _hover: { textDecoration: 'underline' }
            }}
            to={getUrl()}
            external
          >
            <Text fontSize={[12, 14]} color={styles.adress}>
              {`${item.addr1}, ${item.city}`}
            </Text>
          </Link>
        </VStack>
      </GridItem>

      <GridItem
        colStart={{ base: 0, md: 12 }}
        colSpan={{ base: 2, md: 1 }}
        display={{
          base: shouldDisplayDistance ? 'block' : 'none',
          md: 'block'
        }}
        justifyContent='flex-end'
      >
        <Flex justifyContent={{ base: 'flex-start', md: 'center' }}>
          <Stack
            as={Link}
            to={getUrl()}
            external
            flexDirection={{ base: 'row', md: 'column' }}
            sx={{
              svg: {
                height: { base: '18px', md: 30 },
                width: { base: '18px', md: 30 }
              }
            }}
            _hover={{ opacity: 0.5 }}
            w='full'
            gap={{ base: 2, md: 1 }}
          >
            <MapIcon height={30} color={styles.mapIconColor} />
            <Text
              fontSize={{ base: '9px', md: '11px' }}
              color={styles.distance}
              textTransform='uppercase'
              textAlign='center'
            >
              {distance}
            </Text>
          </Stack>
        </Flex>
      </GridItem>
    </Grid>
  ) : null;
};

export default memo(LocationItem);
